import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  CreditCard,
  DollarSign,
  Calendar,
  FileText,
  AlertTriangle,
} from "lucide-react";
import { EnhancedInvoice, PaymentRecordFormData } from "@/types/invoice";
import { paymentRecordFormSchema } from "@/types/validation";

interface PaymentRecordFormProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: EnhancedInvoice;
  onSubmit: (data: PaymentRecordFormData) => Promise<void>;
  loading?: boolean;
}

const PaymentRecordForm: React.FC<PaymentRecordFormProps> = ({
  isOpen,
  onClose,
  invoice,
  onSubmit,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<PaymentRecordFormData>({
    resolver: zodResolver(paymentRecordFormSchema),
    defaultValues: {
      invoiceId: invoice.id,
      paymentDate: new Date().toISOString().split("T")[0],
      amount: "",
      paymentMethod: "cash",
      referenceNumber: "",
      notes: "",
    },
  });

  const formatCurrency = (amount: number) => {
    return `${amount.toFixed(3)} TND`;
  };

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      const formattedData: PaymentRecordFormData = {
        ...data,
        invoiceId: invoice.id,
        paymentDate: new Date(data.paymentDate),
        amount: parseFloat(data.amount),
      };

      await onSubmit(formattedData);
      form.reset();
      onClose();
    } catch (error) {
      console.error("Error submitting payment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const watchedAmount = form.watch("amount");
  const paymentAmount = parseFloat(watchedAmount) || 0;
  const remainingAmount = invoice.remainingAmount || invoice.totalAmount;
  const willOverpay = paymentAmount > remainingAmount;
  const willFullyPay = paymentAmount === remainingAmount;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            {t("invoices.recordPayment")} - {invoice.invoiceNumber}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Invoice Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("invoices.invoiceSummary")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("invoices.totalAmount")}:
                  </span>
                  <span className="font-medium">
                    {formatCurrency(invoice.totalAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("invoices.paidAmount")}:
                  </span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(invoice.paidAmount || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("invoices.remainingAmount")}:
                  </span>
                  <span className="font-medium text-red-600">
                    {formatCurrency(remainingAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("invoices.paymentStatus")}:
                  </span>
                  <Badge
                    variant={
                      invoice.paymentStatus === "paid" ? "default" : "secondary"
                    }
                  >
                    {t(
                      `invoices.paymentStatus.${
                        invoice.paymentStatus || "unpaid"
                      }`
                    )}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Form */}
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Payment Date */}
                <FormField
                  control={form.control}
                  name="paymentDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {t("invoices.paymentDate")}
                      </FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Payment Method */}
                <FormField
                  control={form.control}
                  name="paymentMethod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("invoices.paymentMethod")}</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t("invoices.selectPaymentMethod")}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="cash">
                            {t("invoices.paymentMethods.cash")}
                          </SelectItem>
                          <SelectItem value="card">
                            {t("invoices.paymentMethods.card")}
                          </SelectItem>
                          <SelectItem value="mobile">
                            {t("invoices.paymentMethods.mobile")}
                          </SelectItem>
                          <SelectItem value="bank_transfer">
                            {t("invoices.paymentMethods.bankTransfer")}
                          </SelectItem>
                          <SelectItem value="check">
                            {t("invoices.paymentMethods.check")}
                          </SelectItem>
                          <SelectItem value="other">
                            {t("invoices.paymentMethods.other")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Payment Amount */}
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      {t("invoices.paymentAmount")}
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.001"
                          min="0"
                          placeholder="0.000"
                          {...field}
                        />
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-muted-foreground">
                          TND
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />

                    {/* Payment Amount Validation */}
                    {paymentAmount > 0 && (
                      <div className="space-y-1">
                        {willOverpay && (
                          <div className="flex items-center gap-2 text-sm text-yellow-600">
                            <AlertTriangle className="h-4 w-4" />
                            {t("invoices.overpaymentWarning", {
                              excess: formatCurrency(
                                paymentAmount - remainingAmount
                              ),
                            })}
                          </div>
                        )}
                        {willFullyPay && !willOverpay && (
                          <div className="flex items-center gap-2 text-sm text-green-600">
                            <CreditCard className="h-4 w-4" />
                            {t("invoices.fullPaymentNotice")}
                          </div>
                        )}
                        <div className="text-sm text-muted-foreground">
                          {t("invoices.afterPayment")}:{" "}
                          {formatCurrency(
                            Math.max(0, remainingAmount - paymentAmount)
                          )}
                        </div>
                      </div>
                    )}
                  </FormItem>
                )}
              />

              {/* Quick Amount Buttons */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    form.setValue("amount", remainingAmount.toString())
                  }
                >
                  {t("invoices.payFull")} ({formatCurrency(remainingAmount)})
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    form.setValue("amount", (remainingAmount / 2).toString())
                  }
                >
                  {t("invoices.payHalf")} ({formatCurrency(remainingAmount / 2)}
                  )
                </Button>
              </div>

              {/* Reference Number */}
              <FormField
                control={form.control}
                name="referenceNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("invoices.referenceNumber")} ({t("common.optional")})
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("invoices.referenceNumberPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      {t("invoices.paymentNotes")} ({t("common.optional")})
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("invoices.paymentNotesPlaceholder")}
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={form.handleSubmit(handleSubmit)}
            disabled={isSubmitting || loading}
          >
            {isSubmitting
              ? t("common.processing")
              : t("invoices.recordPayment")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentRecordForm;
